// Demo Polish D5 — Engine page.
//
// Two panels:
//   1. Stats: entry count, total bytes, max_bytes budget, and a table
//      of materialised entries (family, anchor, operator, rows, bytes,
//      access count, age).
//   2. Recommendations: promotion candidates from
//      coframe.metric_engine.promotion_recommendations, each with a
//      paste-able YAML stanza extending the family's cache_hint.
//
// Both fetched on mount + refreshable via a Refresh button. Threshold
// inputs on the recommendations panel let the demo viewer relax the
// 100-hit / 7-day defaults to see candidates emerge from a fresh
// engine.
//
// When the installation has opted out (metric_engine.enabled=false)
// or the workbench wasn't built with an engine_registry, both
// endpoints return enabled=false and the page renders an explicit
// "engine off" state.

import { useCallback, useEffect, useState } from "react";
import {
  type EngineRecommendation,
  type EngineRecommendationsResponse,
  type EngineStatsResponse,
} from "../api/client";
import { useSession } from "./SessionContext";

export function EnginePage() {
  const { client, session } = useSession();
  const [stats, setStats] = useState<EngineStatsResponse | null>(null);
  const [recs, setRecs] = useState<EngineRecommendationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recommendation thresholds — defaults match the engine's defaults.
  const [minHits, setMinHits] = useState(100);
  const [minAgeDays, setMinAgeDays] = useState(7);
  const [minHitsPerDay, setMinHitsPerDay] = useState(1);

  const refresh = useCallback(async () => {
    if (session === null) return;
    setLoading(true);
    setError(null);
    try {
      const [s, r] = await Promise.all([
        client.getEngineStats(session.session_id),
        client.getEngineRecommendations(session.session_id, {
          min_hits: minHits,
          min_age_days: minAgeDays,
          min_hits_per_day: minHitsPerDay,
        }),
      ]);
      setStats(s);
      setRecs(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client, session, minHits, minAgeDays, minHitsPerDay]);

  useEffect(() => {
    setStats(null);
    setRecs(null);
    setError(null);
    if (session !== null) refresh();
  }, [session, refresh]);

  if (session === null) {
    return (
      <section>
        <h1>Engine</h1>
        <p style={subtitleStyle}>
          Open a session in the <strong>Workbench</strong> first — the Engine
          page reads the active session's installation to talk to its
          MetricEngine.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h1>Engine</h1>
      <p style={subtitleStyle}>
        Per-AC Metric Engine state for this installation. Memoised entries
        + promotion recommendations from observed access stats (design
        doc §9.3).
      </p>

      <div style={refreshRowStyle}>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          style={loading ? buttonDisabledStyle : buttonStyle}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        {stats !== null && (
          <span style={mutedStyle}>
            installation: <code>{stats.installation_id}</code>
            {stats.enabled ? (
              stats.caching === false ? (
                <span style={cachingOffBadgeStyle}>caching off (ephemeral)</span>
              ) : (
                <span style={engineOnBadgeStyle}>engine on</span>
              )
            ) : (
              <span style={engineOffBadgeStyle}>not wired</span>
            )}
          </span>
        )}
      </div>

      {error !== null && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <StatsPanel stats={stats} />
      <RecommendationsPanel
        recs={recs}
        thresholds={{
          min_hits: minHits,
          min_age_days: minAgeDays,
          min_hits_per_day: minHitsPerDay,
        }}
        onChangeMinHits={setMinHits}
        onChangeMinAgeDays={setMinAgeDays}
        onChangeMinHitsPerDay={setMinHitsPerDay}
        onApply={refresh}
      />
    </section>
  );
}

// ── Stats panel ─────────────────────────────────────────────────────────

function StatsPanel({ stats }: { stats: EngineStatsResponse | null }) {
  if (stats === null) {
    return (
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>Manifest</div>
        <p style={mutedStyle}>Loading…</p>
      </div>
    );
  }
  if (!stats.enabled) {
    return (
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>Manifest</div>
        <p style={mutedStyle}>
          The Metric Engine isn't wired into this deployment, so there's no
          manifest to show. The engine is always in the query path when the
          serving app is started with an engine store — this workbench
          instance was launched without one (or no AC is loaded for this
          session). Results are unaffected; you just don't get the cache.
        </p>
      </div>
    );
  }
  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Manifest</span>
        <span style={countBadgeStyle}>{stats.entry_count} entries</span>
      </div>
      <div style={kpiRowStyle}>
        <Kpi label="Entries" value={String(stats.entry_count)} />
        <Kpi label="Used" value={formatBytes(stats.total_byte_size)} />
        <Kpi
          label="Budget"
          value={
            stats.max_bytes !== null ? formatBytes(stats.max_bytes) : "unlimited"
          }
          hint={
            stats.max_bytes !== null
              ? `${percent(stats.total_byte_size, stats.max_bytes)} of budget`
              : undefined
          }
        />
      </div>
      {stats.entries.length === 0 ? (
        <p style={mutedStyle}>
          No materialised entries yet. Run a Frame-QL query (the engine
          path memoises results), or invoke <code>pre_materialise()</code>
          to pre-fill the family <code>cache_hint</code> grains.
        </p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Family</th>
              <th style={thStyle}>Anchor</th>
              <th style={thStyle}>Op</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Rows</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Bytes</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Hits</th>
              <th style={thStyle}>Age</th>
            </tr>
          </thead>
          <tbody>
            {stats.entries.map((e, i) => (
              <tr key={i} style={i % 2 ? rowAltStyle : undefined}>
                <td style={tdStyle}>
                  <code>{e.dataset_id}</code>
                </td>
                <td style={tdStyle}>
                  <code>{formatAnchor(e.anchor)}</code>
                </td>
                <td style={tdStyle}>{e.operator ?? "—"}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  {e.row_count.toLocaleString()}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  {formatBytes(e.byte_size)}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  {e.access_count}
                </td>
                <td style={tdStyle}>{formatAge(e.materialized_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Recommendations panel ───────────────────────────────────────────────

interface RecsProps {
  recs: EngineRecommendationsResponse | null;
  thresholds: {
    min_hits: number;
    min_age_days: number;
    min_hits_per_day: number;
  };
  onChangeMinHits: (n: number) => void;
  onChangeMinAgeDays: (n: number) => void;
  onChangeMinHitsPerDay: (n: number) => void;
  onApply: () => void;
}

function RecommendationsPanel({
  recs,
  thresholds,
  onChangeMinHits,
  onChangeMinAgeDays,
  onChangeMinHitsPerDay,
  onApply,
}: RecsProps) {
  if (recs === null) {
    return (
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>Promotion recommendations</div>
        <p style={mutedStyle}>Loading…</p>
      </div>
    );
  }
  if (!recs.enabled) {
    return null;   // already covered by the StatsPanel's engine-off message
  }

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Promotion recommendations</span>
        <span style={countBadgeStyle}>
          {recs.recommendations.length} candidate
          {recs.recommendations.length === 1 ? "" : "s"}
        </span>
      </div>

      <div style={thresholdRowStyle}>
        <ThresholdInput
          label="min hits"
          value={thresholds.min_hits}
          onChange={onChangeMinHits}
          step={10}
        />
        <ThresholdInput
          label="min age (days)"
          value={thresholds.min_age_days}
          onChange={onChangeMinAgeDays}
          step={1}
        />
        <ThresholdInput
          label="min hits/day"
          value={thresholds.min_hits_per_day}
          onChange={onChangeMinHitsPerDay}
          step={0.5}
        />
        <button type="button" onClick={onApply} style={buttonSmallStyle}>
          Apply
        </button>
      </div>

      {recs.recommendations.length === 0 ? (
        <p style={mutedStyle}>
          No promotion candidates under these thresholds. Lower the bar
          (especially <em>min hits</em> and <em>min age</em>) to see
          freshly-warmed entries, or come back after the cache has
          accumulated more access history.
        </p>
      ) : (
        <ul style={recsListStyle}>
          {recs.recommendations.map((r, i) => (
            <RecommendationCard key={i} rec={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RecommendationCard({ rec }: { rec: EngineRecommendation }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    void navigator.clipboard.writeText(rec.suggested_yaml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <li style={recCardStyle}>
      <div style={recHeaderStyle}>
        <code style={{ fontWeight: 600 }}>
          {rec.dataset_id} @ {formatAnchor(rec.anchor)}
        </code>
        <span style={recStatStyle}>
          {rec.access_count} hits / {rec.age_days.toFixed(1)} days =
          {" "}
          <strong>{rec.hits_per_day.toFixed(2)}/day</strong>
        </span>
      </div>
      <pre style={recYamlStyle}>{rec.suggested_yaml}</pre>
      <button type="button" onClick={handleCopy} style={buttonSmallStyle}>
        {copied ? "Copied!" : "Copy YAML"}
      </button>
    </li>
  );
}

// ── Small helpers ────────────────────────────────────────────────────────

function Kpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div style={kpiCellStyle}>
      <div style={kpiLabelStyle}>{label}</div>
      <div style={kpiValueStyle}>{value}</div>
      {hint && <div style={kpiHintStyle}>{hint}</div>}
    </div>
  );
}

function ThresholdInput({
  label,
  value,
  onChange,
  step,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  step: number;
}) {
  return (
    <label style={thresholdLabelStyle}>
      <span style={thresholdLabelTextStyle}>{label}</span>
      <input
        type="number"
        min={0}
        step={step}
        value={value}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          if (!Number.isNaN(n)) onChange(n);
        }}
        style={thresholdInputStyle}
      />
    </label>
  );
}

function formatAnchor(anchor: string[]): string {
  return anchor.length === 0 ? "()" : `(${anchor.join(", ")})`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function percent(part: number, whole: number): string {
  if (whole === 0) return "0%";
  const pct = (part / whole) * 100;
  if (pct < 0.01) return "< 0.01%";
  return `${pct.toFixed(2)}%`;
}

function formatAge(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.max(0, (now - then) / 1000);
  if (secs < 60) return `${Math.floor(secs)}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

// ── Styles ──────────────────────────────────────────────────────────────

const subtitleStyle: React.CSSProperties = {
  color: "#666",
  fontSize: "0.9rem",
  marginTop: "-0.5rem",
};
const refreshRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  margin: "0.75rem 0",
};
const panelStyle: React.CSSProperties = {
  border: "1px solid #e2e2e2",
  borderRadius: 4,
  padding: "0.85rem",
  background: "#fff",
  marginBottom: "1rem",
};
const panelHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "#333",
  marginBottom: "0.6rem",
};
const kpiRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "1rem",
  marginBottom: "1rem",
};
const kpiCellStyle: React.CSSProperties = {
  flex: 1,
  padding: "0.5rem 0.75rem",
  background: "#fafafa",
  border: "1px solid #eee",
  borderRadius: 3,
};
const kpiLabelStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#666",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};
const kpiValueStyle: React.CSSProperties = {
  fontSize: "1.1rem",
  fontWeight: 600,
  color: "#222",
  marginTop: "0.15rem",
};
const kpiHintStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  marginTop: "0.15rem",
};
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.85rem",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.4rem 0.6rem",
  borderBottom: "2px solid #ddd",
  background: "#fafafa",
  color: "#333",
  fontWeight: 600,
};
const tdStyle: React.CSSProperties = {
  padding: "0.3rem 0.6rem",
  borderBottom: "1px solid #eee",
  color: "#222",
  whiteSpace: "nowrap",
};
const rowAltStyle: React.CSSProperties = {
  background: "#fcfcfc",
};
const countBadgeStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  background: "#eee",
  borderRadius: 3,
  padding: "0.05rem 0.4rem",
  fontWeight: 600,
};
const engineOffBadgeStyle: React.CSSProperties = {
  marginLeft: "0.5rem",
  fontSize: "0.7rem",
  color: "#a06b00",
  background: "#fff5d6",
  border: "1px solid #e6d8a8",
  borderRadius: 3,
  padding: "0.05rem 0.4rem",
  fontWeight: 600,
};
const engineOnBadgeStyle: React.CSSProperties = {
  marginLeft: "0.5rem",
  fontSize: "0.7rem",
  color: "#1a7f37",
  background: "#e6f6ea",
  border: "1px solid #b4ddc0",
  borderRadius: 3,
  padding: "0.05rem 0.4rem",
  fontWeight: 600,
};
const cachingOffBadgeStyle: React.CSSProperties = {
  marginLeft: "0.5rem",
  fontSize: "0.7rem",
  color: "#5a4b8a",
  background: "#efeaf9",
  border: "1px solid #cfc3ea",
  borderRadius: 3,
  padding: "0.05rem 0.4rem",
  fontWeight: 600,
};
const mutedStyle: React.CSSProperties = {
  color: "#666",
  fontSize: "0.85rem",
};
const buttonStyle: React.CSSProperties = {
  padding: "0.35rem 0.9rem",
  borderRadius: 3,
  border: "1px solid #0366d6",
  background: "#0366d6",
  color: "white",
  cursor: "pointer",
  fontSize: "0.85rem",
};
const buttonSmallStyle: React.CSSProperties = {
  padding: "0.25rem 0.6rem",
  borderRadius: 3,
  border: "1px solid #0366d6",
  background: "#fff",
  color: "#0366d6",
  cursor: "pointer",
  fontSize: "0.8rem",
};
const buttonDisabledStyle: React.CSSProperties = {
  ...buttonStyle,
  background: "#f4f4f4",
  color: "#aaa",
  borderColor: "#ccc",
  cursor: "not-allowed",
};
const thresholdRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.75rem",
  alignItems: "flex-end",
  flexWrap: "wrap",
  marginBottom: "0.75rem",
  padding: "0.5rem",
  background: "#fafafa",
  border: "1px solid #eee",
  borderRadius: 3,
};
const thresholdLabelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem",
};
const thresholdLabelTextStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#666",
};
const thresholdInputStyle: React.CSSProperties = {
  width: "5.5rem",
  padding: "0.25rem 0.4rem",
  border: "1px solid #ddd",
  borderRadius: 3,
  fontSize: "0.85rem",
};
const recsListStyle: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
};
const recCardStyle: React.CSSProperties = {
  padding: "0.6rem",
  border: "1px solid #d8e8d8",
  background: "#f2faf4",
  borderRadius: 3,
};
const recHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: "1rem",
  marginBottom: "0.4rem",
};
const recStatStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#444",
};
const recYamlStyle: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.78rem",
  background: "#fff",
  border: "1px solid #eee",
  borderRadius: 3,
  padding: "0.5rem",
  margin: "0 0 0.4rem",
  overflowX: "auto",
  whiteSpace: "pre",
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  marginTop: "0.5rem",
  background: "#fdecea",
  color: "#a23",
  border: "1px solid #f5c6cb",
  borderRadius: 4,
  fontSize: "0.85rem",
};
